/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { create } from 'zustand'
import { UserService } from '@/lib/user-service'
import { auth } from '@/lib/firebase'

export interface LogEntry {
  id: string
  timestamp: number
  type: 'request' | 'response' | 'error' | 'info' | 'tool_call'
  provider: string
  model: string
  message: string
  toolCalls?: Array<{ name: string; args: any }>
  details?: {
    promptTokens?: number
    completionTokens?: number
    totalTokens?: number
    cost?: number
    duration?: number
  }
}

interface LogsState {
  logs: LogEntry[]
  maxLogs: number
  addLog: (log: Omit<LogEntry, 'id' | 'timestamp'>) => void
  clearLogs: () => void
}

export const useLogsStore = create<LogsState>((set) => ({
  logs: [],
  maxLogs: 100, // Keep last 100 logs

  addLog: (log) => {
    const entry: LogEntry = {
      ...log,
      id: `log-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
    }

    // Track usage in Firestore for responses
    if (log.type === 'response' && log.details?.totalTokens) {
      const user = auth.currentUser
      if (user) {
        UserService.trackUsage(user.uid, {
          provider: log.provider,
          model: log.model,
          tokens: log.details.totalTokens,
          cost: log.details.cost || 0,
        })
      }
    }

    set((state) => {
      const newLogs = [entry, ...state.logs].slice(0, state.maxLogs)
      return { logs: newLogs }
    })
  },

  clearLogs: () => set({ logs: [] }),
}))

